import { GOOGLE_CONFIG } from '../config/google';

/** Google access tokens are often ~1h; some responses quote ~30m */
export const DEFAULT_ACCESS_TOKEN_TTL_SEC = 3600;

/** Proactively refresh this long before expiry so API calls don’t fail. */
export const ACCESS_TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

const PKCE_VERIFIER_KEY = 'deckpack_oauth_pkce_verifier';
const OAUTH_STATE_KEY = 'deckpack_oauth_state';

const TOKEN_ENDPOINT = 'https://oauth2.googleapis.com/token';
const AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';

/** OAuth redirect path — add exact URLs in Google Cloud Console (see comment in google.js). */
export function getOAuthRedirectUri() {
  const base = process.env.REACT_APP_GOOGLE_OAUTH_REDIRECT_URI;
  if (base && typeof base === 'string' && base.trim()) {
    return base.trim();
  }
  return `${window.location.origin}/oauth-callback`;
}

/**
 * When set (e.g. `/google-token-proxy` on Cloudflare Pages, from wrangler.toml [vars]), token
 * requests go through your server so `client_secret` never ships in the JS bundle.
 */
function getGoogleTokenProxyUrl() {
  const raw = process.env.REACT_APP_GOOGLE_TOKEN_PROXY_URL;
  if (!raw || typeof raw !== 'string' || !raw.trim()) return '';
  const s = raw.trim();
  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  const path = s.startsWith('/') ? s : `/${s}`;
  return `${window.location.origin}${path}`;
}

async function postGoogleToken(formBody) {
  const proxyUrl = getGoogleTokenProxyUrl();
  if (proxyUrl) {
    const json = Object.fromEntries(formBody.entries());
    const res = await fetch(proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(json),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data.error_description || data.error || `Token request failed (${res.status})`;
      throw new Error(msg);
    }
    if (!data.access_token) {
      throw new Error('No access_token in token response');
    }
    return data;
  }

  const clientSecret = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_SECRET;
  if (clientSecret && String(clientSecret).length > 0) {
    formBody.set('client_secret', clientSecret);
  }

  const res = await fetch(TOKEN_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formBody.toString(),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error_description || data.error || `Token request failed (${res.status})`;
    throw new Error(msg);
  }
  if (!data.access_token) {
    throw new Error('No access_token in token response');
  }
  return data;
}

const CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

function randomPkceVerifier() {
  const len = 64;
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, (x) => CHARSET[x % CHARSET.length]).join('');
}

function randomState() {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

function base64UrlEncodeBuffer(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256Pkce(asciiVerifier) {
  const data = new TextEncoder().encode(asciiVerifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncodeBuffer(hash);
}

/**
 * Start Google OAuth 2.0 authorization code flow with PKCE (same tab redirect).
 * Stores verifier + state in sessionStorage. Requires /oauth-callback route.
 */
export async function beginGoogleOAuthCodeFlow() {
  const verifier = randomPkceVerifier();
  const challenge = await sha256Pkce(verifier);
  const state = randomState();
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  const redirectUri = getOAuthRedirectUri();
  const params = new URLSearchParams({
    client_id: GOOGLE_CONFIG.CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: GOOGLE_CONFIG.SCOPES,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    access_type: 'offline',
    /** Improves chance Google returns a refresh_token on sign-in. */
    prompt: 'consent',
    /** Incremental auth / Project Checkup: honored on consent; refresh tokens stay valid when adding scopes later. */
    include_granted_scopes: 'true',
  });

  window.location.assign(`${AUTH_ENDPOINT}?${params.toString()}`);
}

/**
 * Exchange auth code for tokens (call from /oauth-callback only).
 * @param {string} code
 * @param {string} stateFromUrl
 */
export async function exchangeAuthorizationCode(code, stateFromUrl) {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);

  if (!verifier || !expectedState || stateFromUrl !== expectedState) {
    throw new Error('Invalid OAuth state — try signing in again.');
  }

  const redirectUri = getOAuthRedirectUri();
  const body = new URLSearchParams({
    code,
    client_id: GOOGLE_CONFIG.CLIENT_ID,
    code_verifier: verifier,
    grant_type: 'authorization_code',
    redirect_uri: redirectUri,
  });

  return postGoogleToken(body);
}

/**
 * @param {Record<string, unknown>} response - OAuth token endpoint JSON
 */
export function accessTokenExpiresAtFromResponse(response) {
  const sec =
    typeof response?.expires_in === 'number' && response.expires_in > 0
      ? response.expires_in
      : DEFAULT_ACCESS_TOKEN_TTL_SEC;
  return Date.now() + sec * 1000;
}

/**
 * Refresh access token using a refresh_token (no user interaction).
 * @param {string} refreshToken
 * @returns {Promise<Record<string, unknown>>} token response (access_token, expires_in, optional refresh_token)
 */
export async function refreshAccessTokenWithRefreshToken(refreshToken) {
  if (!refreshToken || typeof refreshToken !== 'string') {
    throw new Error('Missing refresh_token');
  }

  const body = new URLSearchParams({
    client_id: GOOGLE_CONFIG.CLIENT_ID,
    refresh_token: refreshToken,
    grant_type: 'refresh_token',
  });

  return postGoogleToken(body);
}

/**
 * GIS token client — no refresh_token; used when legacy session has no refresh_token
 * or as last-resort fallback after refresh failure.
 * @param {{ prompt?: string }} [options]
 */
export function requestGoogleAccessToken({ prompt = '' } = {}) {
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts?.oauth2) {
      reject(new Error('Google Identity not available'));
      return;
    }
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CONFIG.CLIENT_ID,
      scope: GOOGLE_CONFIG.SCOPES,
      include_granted_scopes: true,
      prompt,
      callback: (response) => {
        if (response?.access_token) {
          resolve(response);
        } else {
          reject(new Error(response?.error || 'No access token'));
        }
      },
    });
    try {
      tokenClient.requestAccessToken();
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Merge OAuth token response into stored user (keeps profile; keeps refresh_token unless a new one arrives).
 */
export function mergeTokenResponseIntoUserAuth(base, response) {
  const next = {
    ...base,
    access_token: response.access_token,
    accessTokenExpiresAt: accessTokenExpiresAtFromResponse(response),
    /** App-level session hint (Drive token is the limiting factor). */
    expiry: Date.now() + 7 * 24 * 60 * 60 * 1000,
  };
  if (response.refresh_token) {
    next.refresh_token = response.refresh_token;
  }
  return next;
}

/**
 * Try refresh_token; on failure optionally GIS silent.
 * @param {Record<string, unknown>} userAuth - parsed user_auth object
 * @returns {Promise<Record<string, unknown>>} updated user auth fields to merge
 */
export async function renewAccessTokenForUser(userAuth) {
  const base = userAuth && typeof userAuth === 'object' ? userAuth : {};
  if (base.refresh_token && typeof base.refresh_token === 'string') {
    try {
      const data = await refreshAccessTokenWithRefreshToken(base.refresh_token);
      return mergeTokenResponseIntoUserAuth(base, data);
    } catch (err) {
      console.warn('Refresh token exchange failed, trying GIS silent', err);
    }
  }
  const response = await requestGoogleAccessToken({ prompt: '' });
  return mergeTokenResponseIntoUserAuth(base, response);
}

/**
 * Serialize auth for child `window.open` URLs. Never includes refresh_token (history / logs / referrers).
 * @returns {string} encodeURIComponent(JSON.stringify(...))
 */
export function encodeShareableAuthForQuery() {
  const raw = localStorage.getItem('user_auth');
  if (!raw) return encodeURIComponent('{}');
  const u = JSON.parse(raw);
  const shareable = {
    access_token: u.access_token,
    accessTokenExpiresAt: u.accessTokenExpiresAt,
    expiry: u.expiry,
    name: u.name,
    email: u.email,
    imageUrl: u.imageUrl,
  };
  return encodeURIComponent(JSON.stringify(shareable));
}

/**
 * Merge auth from a child window URL into localStorage without dropping refresh_token from the primary tab.
 */
export function mergeAuthFromChildWindowUrlParam(authParam) {
  if (!authParam) return;
  let fromUrl;
  try {
    fromUrl = JSON.parse(decodeURIComponent(authParam));
  } catch {
    return;
  }
  const saved = localStorage.getItem('user_auth');
  const existing = saved ? JSON.parse(saved) : {};
  const merged = {
    ...existing,
    ...fromUrl,
    refresh_token: existing.refresh_token || fromUrl.refresh_token || null,
  };
  if (!merged.refresh_token) delete merged.refresh_token;
  localStorage.setItem('user_auth', JSON.stringify(merged));
}
